import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import TableContainer from "Components/Common/TableContainer";
import {
  adminGetAllCollaborationHistory,
  adminGetCompanyDocuments,
} from "helpers/backend_helpers";
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Input,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import CollaborationPreview from "./CollaborationPreview";

const CollaborationList = () => {
  const [tableData, setTableData] = useState<any>([]);
  const [data, setData] = useState<any>();
  const [sort, setSort] = useState("createTime");
  const [dir, setDir] = useState(-1);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [previewModal, setPreviewModal] = useState(false);

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>("");
  const [searchName, setSearchName] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchName(filterName);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [filterName]);

  const getData = async () => {
    setLoading(true);
    let query: any = {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sort,
      dir: dir,
    };

    if (searchName) {
      query.projectName = searchName;
    }

    adminGetAllCollaborationHistory(query)
      .then((response: any) => {
        setTableData(
          response.content.map((el: any, index: any) => {
            return {
              ...el,
              index: index + pagination.pageIndex * pagination.pageSize + 1,
            };
          }),
        );
        setRowCount(response.totalElements);
        setTotalPages(response.totalPages);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetFilter = () => {
    setFilterName("");
    setSearchName("");
  };

  const handleToggleFilter = () => {
    if (showFilter) {
      handleResetFilter();
    }
    setShowFilter(!showFilter);
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "index",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Nama Proyek",
        accessorKey: "projectName",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Nilai Proyek",
        accessorKey: "projectValue",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Lokasi Proyek",
        accessorKey: "projectLocation",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Tanggal Mulai",
        accessorKey: "startDate",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Tanggal Selesai",
        accessorKey: "endDate",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Aksi",
        cell: (cellProps: any) => {
          const { _id, filePath, fileName } = cellProps.row.original;
          return (
            <div className="hstack gap-2">
              <UncontrolledTooltip target={`preview-${_id}`}>
                Preview Dokumen
              </UncontrolledTooltip>
              <button
                id={`preview-${_id}`}
                className="btn btn-sm btn-soft-info"
                onClick={() => {
                  setPreviewModal(true);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-eye-line align-bottom" />
              </button>

              <UncontrolledTooltip target={`download-${_id}`}>
                Download Dokumen
              </UncontrolledTooltip>
              <a
                id={`download-${_id}`}
                href={filePath}
                download={fileName}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-soft-primary"
              >
                <i className="ri-download-2-line align-bottom" />
              </a>
            </div>
          );
        },
      },
    ],
    [],
  );

  useEffect(() => {
    getData();
  }, [pagination, sort, dir, searchName]);

  document.title = "Riwayat Kerja Sama";

  return (
    <React.Fragment>
      <div className="page-content">
        <CollaborationPreview
          show={previewModal}
          onCloseClick={() => {
            setPreviewModal(false);
            setData({});
          }}
          data={data}
        />

        <Container fluid>
          <BreadCrumb title="Riwayat Kerja Sama" pageTitle="Manajemen Berkas" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-flex align-items-center">
                    <h5 className="card-title mb-0 flex-grow-1">
                      Riwayat Kerja Sama
                    </h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          className="btn btn-soft-primary btn-sm"
                          onClick={handleToggleFilter}
                        >
                          <i className="ri-filter-3-line align-bottom me-1"></i>
                          Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <Collapse isOpen={showFilter}>
                    <Row className="g-3 mb-4">
                      <Col lg={4}>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Cari nama proyek..."
                          value={filterName}
                          onChange={(e) => setFilterName(e.target.value)}
                        />
                      </Col>
                      <Col lg={12}>
                        <button
                          className="btn btn-soft-secondary btn-sm"
                          onClick={handleResetFilter}
                        >
                          <i className="ri-refresh-line align-bottom me-1"></i>
                          Reset
                        </button>
                      </Col>
                    </Row>
                  </Collapse>
                  <div>
                    {loading ? (
                      <Loader />
                    ) : (
                      <TableContainer
                        columns={columns}
                        data={tableData || []}
                        customPageSize={10}
                        theadClass="text-muted text-uppercase"
                        manualPagination
                        pagination={pagination}
                        pageCount={totalPages}
                        onPaginationChange={setPagination}
                        divClass="table-responsive"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CollaborationList;
